import { User } from 'domains/appDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import {
  JobAdjustmentPrintingCategoryEnum,
  JobAdjustmentPrintingItemViewer,
  JobAdjustmentProcessStatusEnum,
  JobAdjustmentShippingItemViewer,
  JobAdjustmentViewer,
  JobItem,
  JobParticipantEnum,
} from 'domains/workspaceDomain';
import { isBuyer, isSourcer, isSupplier } from 'utils/rbac';
import { TotalRowKeysEnum } from './adjustmentTypes';

interface highlightsByRoleHandleProps {
  data?: JobAdjustmentViewer;
  user?: User | null;
  jobData?: JobItem;
}
interface HighlighterPrintingHandleProps extends highlightsByRoleHandleProps {
  index: number;
  category: JobAdjustmentPrintingItemViewer;
  originalData?: JobAdjustmentViewer;
}

interface HighlighterShippingHandleProps extends highlightsByRoleHandleProps {
  shipping: JobAdjustmentShippingItemViewer;
  originalData?: JobAdjustmentViewer;
  dataIndex?: string;
}

interface HighlightsByTotalHandleProps extends highlightsByRoleHandleProps {
  originalData?: JobAdjustmentViewer;
  dataIndex?: string;
}

export const highlightsByRoleHandle = (props: highlightsByRoleHandleProps) => {
  const { user, data, jobData } = props;
  return (
    (isSourcer(user) && data?.promoterRole === SiteRoleEnum.Supplier) ||
    (isBuyer(user) &&
      (data?.promoterRole === SiteRoleEnum.Sourcer ||
        data?.promoterRole === SiteRoleEnum.Supplier)) ||
    (isSupplier(user) &&
      jobData?.participants.includes(JobParticipantEnum.PROXY) &&
      (data?.promoterRole === SiteRoleEnum.Sourcer ||
        data?.promoterRole === SiteRoleEnum.Supplier) &&
      data.processStatus !== JobAdjustmentProcessStatusEnum.EDITING)
  );
};

export const highlighterPrintingHandle = (
  props: HighlighterPrintingHandleProps
) => {
  const { index, category, originalData } = props;

  const highlightsByRole = highlightsByRoleHandle(props);

  const oldPrinting = originalData?.printingItems.find(
    (item) => item.serialNumber === category.serialNumber
  );

  if (!highlightsByRole) {
    return false;
  }

  if (!oldPrinting) {
    return true;
  }

  const {
    category: oldCategory,
    itemName,
    cost,
    markupPct,
    markup,
    totalCost,
  } = oldPrinting;

  if (oldCategory === JobAdjustmentPrintingCategoryEnum.SHIPPING) {
  }

  switch (index) {
    case 0:
      return oldCategory !== category.category;
    case 1:
      return itemName !== category.itemName;
    case 2:
      return cost !== category.cost;
    case 3:
      return markupPct !== category.markupPct || markup !== category.markup;
    case 4:
      return totalCost !== category.totalCost;
  }
};

export const highlighterShippingHandle = (
  props: HighlighterShippingHandleProps
) => {
  const { originalData, shipping, dataIndex } = props;
  const highlightsByRole = highlightsByRoleHandle(props);
  const oldShipping = originalData?.shippingItems.find(
    (item) => item.serialNumber === shipping.serialNumber
  );

  if (!highlightsByRole) {
    return false;
  }

  if (!oldShipping) {
    return true;
  }
  switch (dataIndex) {
    case 'toName':
      return oldShipping.shippingInfo.toName !== shipping.shippingInfo.toName;
    case 'readyDate':
      return oldShipping.readyDate !== shipping.readyDate;
    case 'accountType':
      return (
        oldShipping.shippingInfo.accountType !==
        shipping.shippingInfo.accountType
      );
    case 'carrierService':
      return (
        oldShipping.shippingInfo.carrierService !==
        shipping.shippingInfo.carrierService
      );
    case 'deliveryDate':
      return oldShipping.deliveryDate !== shipping.deliveryDate;
    case 'expectedDeliveryDate':
      return oldShipping.expectedDeliveryDate !== shipping.expectedDeliveryDate;
    case 'price':
      return oldShipping.price !== shipping.price;
  }
};

export const highlightsByTotalHandle = (
  props: HighlightsByTotalHandleProps
) => {
  const { dataIndex, data, originalData } = props;
  const highlightsByRole = highlightsByRoleHandle(props);
  if (!highlightsByRole) {
    return false;
  }

  switch (dataIndex) {
    case TotalRowKeysEnum.printing:
      return (
        data?.adjustTotal.printingCost !==
        originalData?.adjustTotal.printingCost
      );
    case TotalRowKeysEnum.shipping:
      return (
        data?.adjustTotal.shippingCost !==
        originalData?.adjustTotal.shippingCost
      );
    case TotalRowKeysEnum.taxes:
      return data?.adjustTotal.tax !== originalData?.adjustTotal.tax;
    case TotalRowKeysEnum.totalCharges:
      return (
        data?.adjustTotal.totalCharge !== originalData?.adjustTotal.totalCharge
      );
    case TotalRowKeysEnum.shippingMarkup:
      return (
        data?.adjustTotal.shippingMarkup !==
          originalData?.adjustTotal.shippingMarkup ||
        data?.adjustTotal.shippingMarkupPct !==
          originalData?.adjustTotal.shippingMarkupPct
      );
    case TotalRowKeysEnum.jobMarkup:
      return (
        data?.adjustTotal.printingMarkup !==
          originalData?.adjustTotal.printingMarkup ||
        data?.adjustTotal.printingMarkupPct !==
          originalData?.adjustTotal.printingMarkupPct
      );
  }
};

export const highlighterTitleHandle = (props: HighlightsByTotalHandleProps) => {
  const { dataIndex, data, originalData } = props;
  const highlightsByRole = highlightsByRoleHandle(props);
  if (!highlightsByRole) {
    return false;
  }

  switch (dataIndex) {
    case 'costCenter':
      return originalData?.costCenter !== data?.costCenter;
    case 'accountingLine':
      return originalData?.accountingLine !== data?.accountingLine;
    case 'billingContact':
      return originalData?.billingContact !== data?.billingContact;
    case 'costDistribution':
      return (
        oracleBillingCodesOf(originalData) !== oracleBillingCodesOf(data)
      );
    case 'oracleBillingCode':
      return originalData?.oracleBillingCode !== data?.oracleBillingCode;
  }
};

const oracleBillingCodesOf = (data?: JobAdjustmentViewer) => {
  const { costDistribution } = data || {};
  const oracleStrings = (costDistribution?.oracleSettings || [])
    .map((setting) => setting.oracleString)
    .filter(Boolean);
  if (oracleStrings.length > 0) return oracleStrings.join('|');
  return costDistribution?.manualOracleString?.trim() || '';
};
